import { ethers } from 'ethers';
import { usePrivy } from '@privy-io/react-auth';

import { provider } from '../config/provider.config';
import useUserStore from '../stores/user.store';

const RECEIPT_POLL_INTERVAL_MS = 2000;
const RECEIPT_MAX_ATTEMPTS = 30; // ~60 seconds total
const GAS_ESTIMATE_FALLBACK = ethers.BigNumber.from(300000);
const GAS_ESTIMATE_BUFFER_PCT = 120; // 20% safety buffer on top of estimate

function useWrappedRPC() {
  const { sendTransaction } = usePrivy();
  const user = useUserStore((state) => state.user);

  const pollForReceipt = async (hash) => {
    for (let attempt = 0; attempt < RECEIPT_MAX_ATTEMPTS; attempt++) {
      const receipt = await provider.getTransactionReceipt(hash);
      if (receipt) return receipt;
      await new Promise((resolve) => setTimeout(resolve, RECEIPT_POLL_INTERVAL_MS));
    }
    throw new Error(`Transaction failed on blockchain`);
  };

  const checkRONBalance = async (transactionRequest, maxFeePerGas) => {
    const walletAddress = user?.walletAddress;
    if (!walletAddress) return;

    const balance = await provider.getBalance(walletAddress);
    const txValue = transactionRequest.value
      ? ethers.BigNumber.from(transactionRequest.value)
      : ethers.BigNumber.from(0);

    let gasLimit;
    try {
      gasLimit = await provider.estimateGas({ ...transactionRequest, from: walletAddress });
      gasLimit = gasLimit.mul(GAS_ESTIMATE_BUFFER_PCT).div(100);
    } catch (e) {
      gasLimit = GAS_ESTIMATE_FALLBACK;
      console.warn('[checkRONBalance] gas estimation failed, using fallback:', e.message);
    }

    const gasCost = gasLimit.mul(ethers.BigNumber.from(maxFeePerGas));
    const totalRequired = gasCost.add(txValue);

    if (balance.lt(totalRequired)) {
      const balanceRON = parseFloat(parseFloat(ethers.utils.formatEther(balance)).toFixed(4));
      const requiredRON = parseFloat(parseFloat(ethers.utils.formatEther(totalRequired)).toFixed(4));
      throw new Error(
        `Insufficient RON balance.\n\nYou have ${balanceRON} RON but need at least ${requiredRON} RON to cover this transaction.`
      );
    }
  };

  const sendTransactionWithGas = async (transactionRequest, txUiConfig) => {
    try {
      const { maxFeePerGas, maxPriorityFeePerGas } = await provider.getCustomizedFeeData();

      await checkRONBalance(transactionRequest, maxFeePerGas);

      const tx = {
        ...transactionRequest,
        maxFeePerGas,
        maxPriorityFeePerGas,
      };
      const txReceipt = await sendTransaction(tx, txUiConfig);
      const receipt = await pollForReceipt(txReceipt.hash);

      return { txReceipt, receipt };
    } catch (error) {
      throw error;
    }
  };

  return { sendTransactionWithGas, provider };
}

export default useWrappedRPC;
